import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { Alert, Card, PageHeader, Spinner, useToast } from '@platform/ui';

import { AppointmentTypesView } from '../../features/scheduling/appointment-types-view';
import { useEhrDirectory } from '../../features/scheduling/ehr-link-field';
import { issuesFromApiError, type ValidationIssue } from '../../features/scheduling/validation';
import type { SaveAppointmentTypeInput } from '../../lib/api/scheduling';
import { useSchedulingClient } from '../../lib/api/scheduling-client-context';
import { describeApiError } from '../../lib/session/describe-error';

export const Route = createFileRoute('/appointment-types/')({
  component: AppointmentTypesPage,
});

/**
 * `/appointment-types` — the bookable visit types (MASTER_PLAN M4.11, `Docs/API.md` §2.7).
 *
 * One screen, two endpoints: `POST /admin/appointment-types` for a new type and
 * `PATCH /admin/appointment-types/:id` for an existing one. The dialog emits one payload
 * and this route picks the verb by whether it carries an id (rules §17.1) — `isActive` is
 * on the update schema only, because a type the clinic has just created is active.
 */
function AppointmentTypesPage() {
  const client = useSchedulingClient();
  const queryClient = useQueryClient();
  const ehrDirectory = useEhrDirectory();
  const { toast } = useToast();
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saveIssues, setSaveIssues] = useState<ValidationIssue[]>([]);

  const types = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => client.listAppointmentTypes(),
  });
  const providers = useQuery({ queryKey: ['providers'], queryFn: () => client.listProviders() });

  const save = useMutation({
    mutationFn: ({ id, isActive, ...input }: SaveAppointmentTypeInput) =>
      id === undefined
        ? client.createAppointmentType(input)
        : client.updateAppointmentType(id, { ...input, isActive }),
    onSuccess: async (type) => {
      setSaveError(undefined);
      setSaveIssues([]);
      toast({ tone: 'success', message: `${type.name} saved.` });
      await queryClient.invalidateQueries({ queryKey: ['appointment-types'] });
      await queryClient.invalidateQueries({ queryKey: ['providers'] });
      await queryClient.invalidateQueries({ queryKey: ['availability-preview'] });
    },
    onError: (error: unknown) => {
      setSaveError(describeApiError(error));
      setSaveIssues(issuesFromApiError(error));
    },
  });

  if (types.isPending || providers.isPending) {
    return (
      <>
        <PageHeader title="Appointment types" />
        <Card className="p-6">
          <Spinner label="Loading appointment types" />
        </Card>
      </>
    );
  }

  if (types.isError || providers.isError) {
    return (
      <>
        <PageHeader title="Appointment types" />
        <Alert tone="error">The visit types could not be loaded. Try again in a moment.</Alert>
      </>
    );
  }

  return (
    <AppointmentTypesView
      appointmentTypes={types.data}
      providers={providers.data}
      saveError={saveError}
      ehrDirectory={ehrDirectory}
      serverIssues={saveIssues}
      onSave={(input) => {
        save.mutate(input);
      }}
    />
  );
}
